import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { STATS_ENDPOINT } from '@/lib/analyticsConfig';

export interface StatsSeriesPoint {
  day: string;
  created: number;
  copied: number;
  clicks: number;
  active: number;
}

export interface StatsResponse {
  ok: boolean;
  series: StatsSeriesPoint[];
  templates: Record<string, number>;
  links: Record<string, number>;
  totals: {
    created: number;
    copied: number;
    clicks: number;
    createdDay: number;
    createdWeek: number;
    createdMonth: number;
    copiedWeek: number;
    clicksWeek: number;
    activeToday: number;
    activeWeek: number;
  };
}

/**
 * Fetch aggregate analytics from the admin stats endpoint.
 * Authenticates with a NIP-98-style signed Nostr event (kind 27235) so the
 * server can verify the requester is an authorized admin.
 */
export function useAnalytics() {
  const { user } = useCurrentUser();

  return useQuery<StatsResponse>({
    queryKey: ['analytics', user?.pubkey],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      if (!user) throw new Error('Not logged in');

      const authEvent = await user.signer.signEvent({
        kind: 27235,
        content: '',
        created_at: Math.floor(Date.now() / 1000),
        tags: [
          ['u', STATS_ENDPOINT],
          ['method', 'GET'],
        ],
      });

      const token = btoa(JSON.stringify(authEvent));

      const res = await fetch(STATS_ENDPOINT, {
        method: 'GET',
        headers: { Authorization: `Nostr ${token}` },
      });

      if (res.status === 401) {
        throw new Error('Not authorized. This dashboard is for admins only.');
      }
      if (!res.ok) {
        let detail = '';
        try {
          const body = await res.json();
          detail = body?.detail ? `: ${body.detail}` : '';
        } catch {
          // ignore
        }
        throw new Error(`Failed to load analytics (${res.status})${detail}`);
      }
      return res.json();
    },
  });
}
